import { createApi } from "@reduxjs/toolkit/query/react";
import customFetchBase from "./customFetchBase";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface StartInterviewRequest {
  user_id: string;
  language?: string;
  combined_text?: string;
}

export interface StartInterviewResponse {
  session_id: string;
  stage: string;
  question: string;
  audio_path: string;
}

export interface AnswerInterviewRequest {
  session_id: string;
  question: string;
  answer: string;
}

export interface AnswerInterviewResponse {
  type: string; // "followup" | "next_stage" | "complete"
  stage: string;
  question: string;
  status: string;
  audio_path: string;
}

export interface ExtractAuthorityRequest {
  answer: string;
}

export interface ExtractAuthorityResponse {
  structured_data: {
    industry: string;
    client_type: string;
    topic_clusters: string[];
    problems: string[];
  };
}

export interface CompleteInterviewRequest {
  session_id: string;
}

export interface CompleteInterviewResponse {
  status: string; // "interview_completed"
}

export interface GenerateBlueprintResponse {
  blueprint: string;
}

export interface AuthorityProfileResponse {
  _id: string;
  user_id: string;
  blueprint: string;
}

// ─── API slice (mirrors productApi.ts style) ─────────────────────────────────

export const digitalTwinApi = createApi({
  reducerPath: "digitalTwinApi",
  baseQuery: customFetchBase,
  tagTypes: ["DigitalTwin"],
  endpoints: (builder) => ({

    /** 1. POST /digital-twin/start */
    startInterview: builder.mutation<StartInterviewResponse, StartInterviewRequest>({
      query(data) {
        return {
          url: "digital-twin/start",
          method: "POST",
          body: data,
        };
      },
    }),

    startInterviewTrigger: builder.mutation<
      { result: string; task_id: string },
      StartInterviewRequest
    >({
      query(data) {
        return {
          url: "digital-twin/start_interview_trigger",
          method: "POST",
          body: data,
        };
      },
    }),


    /** 2. POST /digital-twin/interview/answer */
    answerQuestion: builder.mutation<AnswerInterviewResponse, AnswerInterviewRequest>({
      query(data) {
        return {
          url: "digital-twin/interview/answer",
          method: "POST",
          body: data,
        };
      },
    }),

    /** 3. POST /digital-twin/authority/extract */
    extractAuthority: builder.mutation<ExtractAuthorityResponse, ExtractAuthorityRequest>({
      query(data) {
        return {
          url: "digital-twin/authority/extract",
          method: "POST",
          body: data,
        };
      },
    }),

    /** 4. POST /digital-twin/complete */
    completeInterview: builder.mutation<CompleteInterviewResponse, CompleteInterviewRequest>({
      query(data) {
        return {
          url: "digital-twin/complete",
          method: "POST",
          body: data,
        };
      },
    }),

    /** 5. POST /digital-twin/authority/generate?session_id=... */
    generateBlueprint: builder.mutation<GenerateBlueprintResponse, { session_id: string }>({
      query({ session_id }) {
        return {
          url: `digital-twin/authority/generate?session_id=${session_id}`,
          method: "POST",
        };
      },
    }),

    getTaskStatus: builder.query<
    { state: string; result?: any },
    string
  >({
    query(task_id) {
      return {
        url: `prompts/task_status?task_id=${task_id}`,
        method: "GET",
      };
    },
  }),

    /** 6. GET /digital-twin/authority/profile/{user_id} */
    getAuthorityProfile: builder.query<AuthorityProfileResponse, string>({
      query(user_id) {
        return {
          url: `digital-twin/authority/profile/${user_id}`,
          method: "GET",
        };
      },
      providesTags: ["DigitalTwin"],
    }),
  }),
});

export const {
  useStartInterviewMutation,
  useStartInterviewTriggerMutation,
  useLazyGetTaskStatusQuery,
  useAnswerQuestionMutation,
  useExtractAuthorityMutation,
  useCompleteInterviewMutation,
  useGenerateBlueprintMutation,
  useGetAuthorityProfileQuery,
} = digitalTwinApi;